// src/scenarios/useScenarioSession.ts
import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ChatMsg, ChatListing, ContextFile } from 'types';
import { ContextDocumentTargetType as TargetType } from 'types';
import { useAuthStore } from 'store/authStore';
import {
  listChats,
  createChat,
  getChatMessages,
  getChatView,
  sendMessage as sendMessageApi,
  deleteChat as deleteChatApi,
  stopChat as stopChatApi,
  renameChat,
  clearChat,
} from 'services/chatApi';
import { uploadContextFile, deleteContextFile, resolveContextDocumentId } from 'services/filesApi';
import { SCENARIO_CHAT_TASK_ID_KEY, useStreamingMessage } from 'hooks/useStreamingMessage';
import { useObfuscationGate } from 'hooks/useObfuscationGate';
import { extractReuploadFileNames } from 'utils/reuploadError';
import type { ReuploadReplacement } from 'components/ReuploadFilesDialog';

export type SessionStatus = 'idle' | 'running' | 'active';

// Единый источник состава файлов чата — view чата (chat/{id}/view). Он же несёт
// is_active, поэтому отдельный /projects/context-documents?chat_id здесь не нужен.
// До запуска прогона загруженные документы приходят с is_active:false — это норма:
// активность определяется составом use_context_files при запуске, а выбор до этого
// ведётся на фронте (selectedFileIds).
const fetchChatContextDocuments = (chatId: string): Promise<ContextFile[]> =>
  getChatView(chatId).then((v) => v?.context_documents ?? []);

// Якорь на staging-чат текущего (ещё не запущенного) прогона — chat_id черновика
// с загруженными файлами. Живёт в localStorage, чтобы файлы пережили перезагрузку
// до запуска; стирается при запуске прогона и сбросе.
function readStagingChatId(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writeStagingChatId(key: string, id: string | null): void {
  try {
    if (id) localStorage.setItem(key, id);
    else localStorage.removeItem(key);
  } catch {
    // localStorage недоступен — персистентность необязательна
  }
}

export interface UseScenarioSessionResult {
  // State
  status: SessionStatus;
  messages: ChatMsg[];
  streamingMessage: ChatMsg | null;
  displayedContent: string;
  progressLabel: string | null;
  isLoading: boolean;
  // Демо: обфускация данных перед внешней моделью
  obfuscating: boolean;
  modelExternal: boolean;
  // Chat history
  chatId: string | null;
  chatHistory: ChatListing[];
  loadChat: (chatId: string) => Promise<void>;
  deleteChat: (chatId: string) => Promise<void>;
  /** id чата, который сейчас удаляется (для индикации в UI) */
  deletingChatId: string | null;
  /** true пока идёт первичное восстановление сессии (история + сообщения) */
  isRestoring: boolean;
  // Files
  files: ContextFile[];
  /** Активные документы просматриваемого из истории чата (из view); null вне просмотра истории */
  historyFiles: ContextFile[] | null;
  selectedFileIds: string[];
  isFilesLoading: boolean;
  isUploading: boolean;
  uploadError: string | null;
  deletingFileId: string | null;
  /** chat_id, в котором живут файлы сценария (для ReuploadFilesDialog) */
  filesChatId: string | null;
  /** Имена файлов из 422 «загрузите повторно»; null = диалог закрыт */
  reuploadFileNames: string[] | null;
  closeReuploadDialog: () => void;
  /** Заменяет перезагруженные документы в выборе и обновляет листинг */
  handleReuploaded: (replacements: ReuploadReplacement[]) => void;
  // Actions
  selectFile: (fileId: string) => void;
  deselectFile: (fileId: string) => void;
  uploadFiles: (files: File[]) => Promise<void>;
  deleteFile: (fileId: string) => Promise<void>;
  runScenario: (deepAnalysis?: boolean) => Promise<void>;
  sendMessage: (text: string, deepAnalysis?: boolean) => Promise<void>;
  /** Прервать текущую генерацию (запуск сценария или ответ в чате) */
  stopGeneration: () => Promise<void>;
  /** Сбросить первый запуск: остановить, удалить созданный чат, вернуть к выбору файлов (выбор сохраняется) */
  cancelRun: () => Promise<void>;
  resetSession: () => Promise<void>;
}

export function useScenarioSession(
  scenarioId: string,
  scenarioTitle: string,
  _scenarioRole: string,
  /** Максимум файлов в контексте сценария — автоселект не превышает это значение */
  maxFiles: number,
  scenarioSystemPrompt: string | null = null,
  scenarioDocIds: string[] = []
): UseScenarioSessionResult {
  const user = useAuthStore((s) => s.user);
  const projectId: string = user?.projectId ?? '';
  const queryClient = useQueryClient();

  // Ключ для персистентности выбранных файлов — отдельный на каждый сценарий
  const selectionStorageKey = `scenario_selected_files_${scenarioId}`;
  // Якорь на staging-чат текущего (ещё не запущенного) прогона. Каждый прогон
  // стартует с пустого набора файлов; файлы завершённых чатов не подтягиваются.
  // Пока прогон не запущен, якорь хранит chat_id черновика — чтобы загруженные
  // файлы пережили перезагрузку страницы (F5). После запуска/сброса якорь стирается.
  const stagingStorageKey = `scenario_staging_chat_${scenarioId}`;

  const [status, setStatus] = useState<SessionStatus>('idle');
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [chatId, setChatId] = useState<string | null>(null);
  const [chatHistory, setChatHistory] = useState<ChatListing[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // Демо: при запросе к внешней модели имитируем обфускацию данных (см. useObfuscationGate).
  const { obfuscating, external: modelExternal } = useObfuscationGate(isLoading);
  // Восстанавливаем выбор файлов из localStorage, чтобы он переживал обновление
  // страницы — но только если есть черновик текущего прогона (staging-якорь).
  // Без него прогон стартует с чистого листа: ни файлов, ни «висящего» выбора.
  const [selectedFileIds, setSelectedFileIds] = useState<string[]>(() => {
    try {
      if (!localStorage.getItem(stagingStorageKey)) return [];
      const raw = localStorage.getItem(selectionStorageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed)
        ? parsed.filter((id): id is string => typeof id === 'string')
        : [];
    } catch {
      return [];
    }
  });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
  // 422 «документы загружены до обновления — загрузите повторно»: имена файлов для диалога
  const [reuploadFileNames, setReuploadFileNames] = useState<string[] | null>(null);
  const [deletingChatId, setDeletingChatId] = useState<string | null>(null);
  // chatId used for file operations — may be a staging chat (no messages, hidden from history)
  const [filesChatId, setFilesChatId] = useState<string | null>(null);
  // Активные документы просматриваемого из истории чата (view.context_documents с is_active).
  // null = не режим просмотра истории (idle-пикер или свежий прогон). При просмотре
  // старого чата это единый источник списка файлов — без разделения на загруженные и
  // зашитые в сценарий: показываем ровно то, что реально активно в этом чате.
  const [historyFiles, setHistoryFiles] = useState<ContextFile[] | null>(null);
  // true пока не завершилось первичное восстановление сессии — чтобы не моргать idle-видом
  const [isRestoring, setIsRestoring] = useState(true);

  const chatIdRef = useRef<string | null>(null);
  // Зеркало filesChatId + чат, в котором файлы лежали ДО запуска сценария —
  // чтобы при «Сбросить» вернуть выбор файлов в исходный чат, удалив run-чат
  const filesChatIdRef = useRef<string | null>(null);
  const prevFilesChatIdRef = useRef<string | null>(null);

  // Keep refs in sync with state
  useEffect(() => {
    chatIdRef.current = chatId;
  }, [chatId]);

  useEffect(() => {
    filesChatIdRef.current = filesChatId;
  }, [filesChatId]);

  const { streamingMessage, displayedContent, progressLabel, resetStreaming } = useStreamingMessage(
    setMessages,
    setIsLoading,
    SCENARIO_CHAT_TASK_ID_KEY
  );

  // Files are fetched per chat_id (shared across the scenario).
  // staleTime: Infinity — список НЕ рефетчится автоматически при смене filesChatId.
  // Загрузка/удаление файлов асинхронны (обработка завершается WebSocket-событием),
  // поэтому список обновляем вручную (invalidate/fetchQuery) только после события об
  // успешной загрузке. Иначе создание нового чата под загрузку сразу дёргало бы
  // пустой список ещё до того, как файл реально обработан.
  const { data: files = [], isLoading: isFilesLoading } = useQuery<ContextFile[]>({
    queryKey: ['contextFiles', filesChatId],
    queryFn: () => {
      if (filesChatId === null) {
        throw new Error('contextFiles query ran without filesChatId');
      }
      return fetchChatContextDocuments(filesChatId);
    },
    enabled: filesChatId !== null,
    staleTime: Infinity,
  });

  // First User message is always the system prompt trigger — never show it
  const filterTrigger = (msgs: ChatMsg[]) =>
    msgs.length > 0 && msgs[0].role === 'User' ? msgs.slice(1) : msgs;

  // Staging chats have no messages (last_message_at === null) — hide from history
  const filterHistory = (items: ChatListing[]) => items.filter((c) => c.last_message_at !== null);

  // On mount: load chat history (for the History panel) and resolve a chat_id for
  // file operations — but DO NOT auto-open the last chat. Обновление страницы должно
  // оставлять idle-вид «нового сценария» с пикером файлов (галочки восстанавливаются
  // из localStorage), а не перекидывать на просмотр последнего чата. Прошлый разговор
  // пользователь открывает вручную из панели истории.
  useEffect(() => {
    if (!projectId) {
      setIsRestoring(false);
      return;
    }

    let cancelled = false;
    setIsRestoring(true);

    listChats(scenarioId)
      .then((items) => {
        if (cancelled) return;
        const real = filterHistory(items);
        setChatHistory(real);

        // Файлы подтягиваем ТОЛЬКО из черновика текущего прогона (staging-якорь),
        // и только если он всё ещё существует и не запущен (без сообщений).
        // Завершённые чаты открываются вручную из истории — их файлы здесь не нужны.
        const stagingId = readStagingChatId(stagingStorageKey);
        const staging =
          stagingId && items.find((c) => c.id === stagingId && c.last_message_at === null);
        if (staging) {
          setFilesChatId(staging.id);
        } else if (stagingId) {
          // Якорь устарел (чат запущен/удалён) — чистим, стартуем пусто
          writeStagingChatId(stagingStorageKey, null);
        }
      })
      .catch(() => {
        // non-critical: start fresh if history unavailable
      })
      .finally(() => {
        if (!cancelled) setIsRestoring(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioId, projectId]);

  // Transition to ACTIVE when AI responds after run
  useEffect(() => {
    if (status === 'running' && !isLoading && messages.some((m) => m.role === 'AI')) {
      setStatus('active');
    }
  }, [status, isLoading, messages]);

  // Persist selection per scenario so it survives a page refresh.
  // Только в idle: при просмотре прошлого чата selectedFileIds отражают активные
  // документы этого чата (из /chat/{id}/view) — их нельзя писать в выбор нового запуска.
  useEffect(() => {
    if (status !== 'idle') return;
    try {
      if (selectedFileIds.length > 0) {
        localStorage.setItem(selectionStorageKey, JSON.stringify(selectedFileIds));
      } else {
        localStorage.removeItem(selectionStorageKey);
      }
    } catch {
      // localStorage недоступен (приватный режим/квота) — персистентность необязательна
    }
  }, [selectedFileIds, selectionStorageKey, status]);

  // Drop stale file IDs that no longer exist in the list.
  // Пропускаем, пока файлы ещё не загружены (filesChatId === null → запрос отключён,
  // files === []), иначе сотрём восстановленный из localStorage выбор на маунте.
  useEffect(() => {
    if (filesChatId === null || isFilesLoading) return;
    const valid = new Set(files.map((f) => f.id));
    setSelectedFileIds((prev) => {
      const next = prev.filter((id) => valid.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [files, isFilesLoading, filesChatId]);

  // ─── Load a specific chat from history ────────────────────────────────────

  const loadChat = useCallback(
    async (id: string) => {
      // Сообщения и реальный состав документов чата тянем параллельно.
      // view не критичен — если упал, чат всё равно открываем (без подсветки файлов).
      const [history, view] = await Promise.all([
        getChatMessages(id).then(filterTrigger),
        getChatView(id).catch(() => undefined),
      ]);
      setChatId(id);
      setMessages(history);
      // Засеиваем кеш списка тем же view — чтобы useQuery не сделал повторный запрос
      // view при смене filesChatId (staleTime: Infinity → возьмёт из кеша).
      queryClient.setQueryData(['contextFiles', id], view?.context_documents ?? []);
      setFilesChatId(id);
      // «Файлы в работе» слева = активные ЗАГРУЖЕННЫЕ документы этого чата из view
      // (context_documents с is_active). Зашитые в сценарий документы исключаем —
      // они показываются отдельным блоком «Файлы сценария» (как на чистом сценарии),
      // поэтому в общий список их не дублируем.
      const scenarioDocs = new Set(scenarioDocIds);
      const activeDocs = view
        ? (view.context_documents ?? []).filter((d) => d.is_active && !scenarioDocs.has(d.id))
        : [];
      setHistoryFiles(activeDocs);
      setSelectedFileIds(activeDocs.map((d) => d.id));
      // Открытие чата из истории — всегда активный вид (с «Файлами в работе»),
      // а не пикер нового сценария. Даже если после фильтрации триггера сообщений
      // не осталось — показывать пикер для существующего чата неверно.
      setStatus('active');
    },
    [queryClient, scenarioDocIds]
  );

  // ─── Delete a chat from history ───────────────────────────────────────────

  const deleteChat = useCallback(
    async (id: string) => {
      if (deletingChatId) return;
      setDeletingChatId(id);
      try {
        await deleteChatApi(id);

        // Снимаем чат из истории и определяем, на что переключиться
        let nextChatId: string | null = null;
        setChatHistory((prev) => {
          const next = prev.filter((c) => c.id !== id);
          if (chatIdRef.current === id) nextChatId = next[0]?.id ?? null;
          return next;
        });

        // Удалили активный чат — переходим к следующему либо сбрасываем вид
        if (chatIdRef.current === id) {
          resetStreaming();
          if (nextChatId) {
            await loadChat(nextChatId);
          } else {
            setMessages([]);
            setChatId(null);
            chatIdRef.current = null;
            setHistoryFiles(null);
            setStatus('idle');
            setIsLoading(false);
          }
        }
      } finally {
        setDeletingChatId(null);
      }
    },
    [deletingChatId, loadChat, resetStreaming]
  );

  // ─── File selection ────────────────────────────────────────────────────────

  const selectFile = useCallback((fileId: string) => {
    setSelectedFileIds((prev) => (prev.includes(fileId) ? prev : [...prev, fileId]));
  }, []);

  const deselectFile = useCallback((fileId: string) => {
    setSelectedFileIds((prev) => prev.filter((id) => id !== fileId));
  }, []);

  const uploadFiles = useCallback(
    async (fileList: File[]) => {
      const validFiles = fileList.filter((f) => f.name);
      if (validFiles.length === 0) return;

      setIsUploading(true);
      setUploadError(null);
      try {
        // Ensure we have a chat_id for file operations
        let activeChatId = filesChatId;
        if (!activeChatId) {
          // Create a staging chat (hidden from history — no messages/last_message_at)
          const staging = await createChat(projectId, scenarioId);
          if (!staging) {
            setUploadError('Не удалось создать сессию для загрузки файлов');
            return;
          }
          activeChatId = staging.id;
          // Новый чат заведомо пуст — засеиваем кеш, чтобы useQuery показал пустой
          // список из кеша и НЕ делал сетевой запрос до завершения загрузки файла.
          queryClient.setQueryData(['contextFiles', activeChatId], []);
          setFilesChatId(activeChatId);
          // Запоминаем черновик прогона, чтобы файлы пережили перезагрузку до запуска
          writeStagingChatId(stagingStorageKey, activeChatId);
        }

        if (activeChatId === null) {
          return;
        }

        const fileOpsChatId: string = activeChatId;

        for (const file of validFiles) {
          // uploadContextFile резолвится только после WebSocket-события об успешной
          // обработке документа — до этого момента список не трогаем.
          const result = await uploadContextFile(
            projectId,
            { type: TargetType.Chat, id: fileOpsChatId },
            file
          );

          if (result?.error) {
            setUploadError(result.error);
            continue;
          }

          const uploadId = result?.document?.id;
          if (!uploadId) continue;

          // Файл обработан — теперь (и только теперь) тянем актуальный список.
          // fetchQuery обновляет кеш, и useQuery с тем же ключом подхватывает данные.
          const freshFiles =
            (await queryClient.fetchQuery({
              queryKey: ['contextFiles', fileOpsChatId],
              queryFn: () => fetchChatContextDocuments(fileOpsChatId),
            })) ?? [];

          const resolvedId = resolveContextDocumentId(freshFiles, uploadId, file.name);

          setSelectedFileIds((prev) => {
            const withoutRaw = prev.filter((x) => x !== uploadId);
            if (withoutRaw.includes(resolvedId)) return withoutRaw;
            if (withoutRaw.length >= maxFiles) return withoutRaw;
            return [...withoutRaw, resolvedId];
          });
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Ошибка загрузки файла';
        setUploadError(msg);
      } finally {
        setIsUploading(false);
      }
    },
    [filesChatId, projectId, scenarioId, queryClient, maxFiles, stagingStorageKey]
  );

  const deleteFile = useCallback(
    async (fileId: string) => {
      setDeletingFileId(fileId);
      try {
        await deleteContextFile(fileId);
        if (filesChatId) {
          await queryClient.invalidateQueries({ queryKey: ['contextFiles', filesChatId] });
        }
        setSelectedFileIds((prev) => prev.filter((id) => id !== fileId));
      } finally {
        setDeletingFileId(null);
      }
    },
    [filesChatId, queryClient]
  );

  // ─── Run scenario (create new chat) ───────────────────────────────────────

  const runScenario = useCallback(
    async (deepAnalysis = false) => {
      if (!user || status !== 'idle') return;
      if (!projectId) return;

      resetStreaming();
      setIsLoading(true);

      try {
        const now = new Date();
        const runLabel = `${now.toLocaleDateString('ru', { day: '2-digit', month: '2-digit', year: 'numeric' })}, ${now.toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })}`;
        const runTitle = `${scenarioTitle} — ${runLabel}`;

        // Один чат на весь прогон: переиспользуем чат, в который грузили файлы
        // (staging), как чат прогона — туда же уходит и стартовое сообщение.
        // Отдельный чат создаём только если файлов не было (например min_files = 0).
        const existingChatId = filesChatIdRef.current;
        let runChatId: string;
        if (existingChatId) {
          runChatId = existingChatId;
          // Переименовываем черновик в нормальный заголовок прогона для истории
          void renameChat(runChatId, runTitle).catch(() => undefined);
        } else {
          const chat = await createChat(projectId, scenarioId, runTitle);
          if (!chat) {
            setIsLoading(false);
            return;
          }
          runChatId = chat.id;
        }

        setChatId(runChatId);
        chatIdRef.current = runChatId;
        // Запоминаем чат с файлами до запуска — нужен для отката в cancelRun
        prevFilesChatIdRef.current = filesChatIdRef.current;
        setFilesChatId(runChatId);
        // Прогон запущен — черновик закрыт, следующий заход стартует пусто
        writeStagingChatId(stagingStorageKey, null);
        setMessages([]);
        // Свежий прогон, а не просмотр истории — список файлов берём из выбора, не из view
        setHistoryFiles(null);
        setStatus('running');

        // Trigger the AI to start the scenario using system_prompt
        const response = await sendMessageApi(runChatId, {
          human_message: scenarioSystemPrompt ?? scenarioTitle,
          // Новый формат бэкенда: {id, headers?}; без выбора заголовков шлём весь документ
          use_context_files: [...scenarioDocIds, ...selectedFileIds].map((id) => ({ id })),
          context_messages_limit: null,
          analysys_mode: deepAnalysis,
        });
        if (response?.task_id) {
          localStorage.setItem(SCENARIO_CHAT_TASK_ID_KEY, response.task_id);
          localStorage.setItem(
            `stream_chat_${response.task_id}`,
            JSON.stringify({ role: 'AI', content: '', isStreaming: true })
          );
        } else {
          setIsLoading(false);
        }

        // Refresh chat history (filter staging chats without messages)
        listChats(scenarioId)
          .then((items) => setChatHistory(filterHistory(items)))
          .catch(() => undefined);
      } catch (err) {
        const names = extractReuploadFileNames(err);
        if (names) setReuploadFileNames(names);
        setIsLoading(false);
      }
    },
    [
      user,
      status,
      projectId,
      scenarioId,
      scenarioTitle,
      scenarioSystemPrompt,
      resetStreaming,
      selectedFileIds,
      scenarioDocIds,
      stagingStorageKey,
    ]
  );

  // ─── Send message ──────────────────────────────────────────────────────────

  const sendMessage = useCallback(
    async (text: string, deepAnalysis = false) => {
      if (status === 'idle' || !text.trim()) return;
      const currentChatId = chatIdRef.current;
      if (!currentChatId) return;

      setIsLoading(true);
      resetStreaming();

      const userMsg: ChatMsg = {
        role: 'User',
        content: text.trim(),
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMsg]);

      try {
        const response = await sendMessageApi(currentChatId, {
          human_message: text.trim(),
          // Новый формат бэкенда: {id, headers?}; без выбора заголовков шлём весь документ
          use_context_files: [...scenarioDocIds, ...selectedFileIds].map((id) => ({ id })),
          context_messages_limit: null,
          analysys_mode: deepAnalysis,
        });
        if (response?.task_id) {
          localStorage.setItem(SCENARIO_CHAT_TASK_ID_KEY, response.task_id);
          localStorage.setItem(
            `stream_chat_${response.task_id}`,
            JSON.stringify({ role: 'AI', content: '', isStreaming: true })
          );
        }
      } catch (err) {
        const names = extractReuploadFileNames(err);
        if (names) setReuploadFileNames(names);
        setIsLoading(false);
      }
    },
    [status, selectedFileIds, resetStreaming, scenarioDocIds]
  );

  // ─── Stop generation (прервать запуск/ответ) ───────────────────────────────

  const stopGeneration = useCallback(async () => {
    // task_id читаем ДО resetStreaming — он стирает ключ из localStorage
    const taskId = localStorage.getItem(SCENARIO_CHAT_TASK_ID_KEY);
    const currentChatId = chatIdRef.current;

    resetStreaming();
    setIsLoading(false);

    if (currentChatId && taskId) {
      try {
        await stopChatApi(currentChatId, taskId);
      } catch {
        // non-critical: бэкенд мог уже завершить задачу
      }
    }
  }, [resetStreaming]);

  // ─── Cancel run (прервать первый запуск, вернуться к выбору файлов) ──────────
  // Возвращает на экран выбора файлов, СОХРАНЯЯ выбор (галочки). Прогон идёт в том
  // же чате, что нёс файлы, поэтому чат НЕ удаляем (потеряли бы документы) — только
  // очищаем отправленное стартовое сообщение, чтобы чат снова стал черновиком
  // (без сообщений → скрыт из истории), сохранив прикреплённые файлы.

  const cancelRun = useCallback(async () => {
    const taskId = localStorage.getItem(SCENARIO_CHAT_TASK_ID_KEY);
    const runChatId = chatIdRef.current;
    // Чат с файлами до запуска. Совпадает с runChatId (один чат на прогон);
    // null — если файлов не было и чат был создан только под прогон.
    const filesChat = prevFilesChatIdRef.current;

    resetStreaming();
    setMessages([]);
    setIsLoading(false);
    setStatus('idle');
    setChatId(null);
    chatIdRef.current = null;
    // Возврат к пикеру нового прогона, а не к просмотру истории
    setHistoryFiles(null);
    // Возвращаем источник файлов на чат-черновик и восстанавливаем staging-якорь,
    // чтобы список/выбор остались валидными и пережили перезагрузку.
    setFilesChatId(filesChat);
    writeStagingChatId(stagingStorageKey, filesChat);

    if (runChatId) {
      if (taskId) {
        try {
          await stopChatApi(runChatId, taskId);
        } catch {
          // non-critical: задача могла уже завершиться
        }
      }
      try {
        if (filesChat) {
          // Чат нёс файлы — очищаем только сообщения, документы остаются.
          // Чат снова становится черновиком текущего прогона.
          await clearChat(runChatId);
        } else {
          // Файлов не было — чат создавался только под прогон, его можно удалить.
          await deleteChatApi(runChatId);
        }
        setChatHistory((prev) => prev.filter((c) => c.id !== runChatId));
      } catch {
        // non-critical: не блокируем возврат на экран выбора
      }
    }
  }, [resetStreaming, stagingStorageKey]);

  // ─── Re-upload устаревших документов (422 «загрузите повторно») ─────────────

  const closeReuploadDialog = useCallback(() => setReuploadFileNames(null), []);

  const handleReuploaded = useCallback(
    (replacements: ReuploadReplacement[]) => {
      const byOldId = new Map(
        replacements
          .filter((r): r is ReuploadReplacement & { oldId: string } => r.oldId !== null)
          .map((r) => [r.oldId, r.newId])
      );
      setSelectedFileIds((prev) => {
        const next = prev.map((id) => byOldId.get(id) ?? id);
        // Новые документы, чьи старые версии не нашлись по имени, добавляем в выбор
        const extras = replacements
          .filter((r) => r.oldId === null && !next.includes(r.newId))
          .map((r) => r.newId);
        return [...next, ...extras];
      });
      if (filesChatId) {
        void queryClient.invalidateQueries({ queryKey: ['contextFiles', filesChatId] });
      }
    },
    [filesChatId, queryClient]
  );

  // ─── Reset session (start fresh — new chat created only on next runScenario) ─

  const resetSession = useCallback(async () => {
    resetStreaming();
    setMessages([]);
    setSelectedFileIds([]);
    setStatus('idle');
    setIsLoading(false);
    setChatId(null);
    chatIdRef.current = null;
    setHistoryFiles(null);
    // Чистый старт нового прогона: ни файлов, ни черновика
    setFilesChatId(null);
    writeStagingChatId(stagingStorageKey, null);
  }, [resetStreaming, stagingStorageKey]);

  return {
    status,
    messages,
    streamingMessage,
    displayedContent,
    progressLabel,
    isLoading,
    obfuscating,
    modelExternal,
    chatId,
    chatHistory,
    loadChat,
    deleteChat,
    deletingChatId,
    isRestoring,
    files,
    historyFiles,
    selectedFileIds,
    isFilesLoading,
    isUploading,
    uploadError,
    deletingFileId,
    filesChatId,
    reuploadFileNames,
    closeReuploadDialog,
    handleReuploaded,
    selectFile,
    deselectFile,
    uploadFiles,
    deleteFile,
    runScenario,
    sendMessage,
    stopGeneration,
    cancelRun,
    resetSession,
  };
}
